import { faCreditCard, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WidgetContainer } from '@frontend/app/refresh-components';
import { cn } from '@frontend/shadcn/lib/utils';
import React from 'react';
import { CircleDollarIcon, SackDollarIcon, ClockIcon, FileIcon, TagsIcon } from '@revfluence/fresh-icons/solid/esm';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { useGetPaymentDashboardWidgetData } from '@frontend/applications/PaymentsApp/ReFreshPayment/hooks';
import { PaymentDashboardFilterInput } from '@frontend/app/types/globalTypes';
import { largeNumberAbbreviatorWithDecimal } from '@frontend/utils';
import { ProjectWidgetContainer } from '../common/ProjectWidgetContainer';

interface PaymentsProps {
  projectId?: number;
  className?: string;
}

const PaymentStatCard = ({
  title,
  value,
  count,
  icon,
  tooltip,
  isLoading
}: {
  title: string;
  value: number;
  count: number;
  icon: React.ReactNode;
  tooltip?: string;
  isLoading: boolean;
}) => {
  const formatCount = (count: number) => {
    return new Intl.NumberFormat('en-US').format(count);
  };

  // Use the same logic as MainMetric for currency formatting
  const valueWithAbbreviation = largeNumberAbbreviatorWithDecimal(value);

  if (isLoading) {
    return (
      <ProjectWidgetContainer
        title={title}
        icon={icon}
        tooltip={tooltip}
        className="w-full"
      >
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>
      </ProjectWidgetContainer>
    );
  }

  return (
    <ProjectWidgetContainer
      title={title}
      icon={icon}
      tooltip={tooltip}
      className="w-full"
    >
      <div className="flex flex-col justify-center items-start gap-2">
        <div className="self-stretch inline-flex justify-start items-center gap-2">
          <div className="flex justify-start items-end gap-1">
            <div className="flex justify-start items-end gap-1">
              <div className="justify-start text-gray-800 text-3xl font-medium font-['Inter'] leading-[48px]">
                $<span>{valueWithAbbreviation.value}</span><span>{valueWithAbbreviation.symbol}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="self-stretch inline-flex justify-start items-center gap-1">
          <div className="justify-center text-gray-500 text-xs font-normal font-['Inter'] leading-tight">
            {formatCount(count)} payments
          </div>
        </div>
      </div>
    </ProjectWidgetContainer>
  );
};

export const Payments = (props: PaymentsProps) => {
  const { projectId, className } = props;

  const filters: PaymentDashboardFilterInput = {
    projectIds: projectId ? [projectId] : undefined,
    paymentStatus: undefined,
    paymentTypes: undefined,
    tags: undefined,
    budgetAccounts: undefined,
    initiatedBy: undefined,
    searchBy: null,
    searchQuery: null,
  };

  const { data, loading, error } = useGetPaymentDashboardWidgetData({
    variables: { filters },
    skip: !projectId, // Skip query if no projectId
  });

  const paymentData = data?.paymentSummary;
  const isLoading = loading;

  if (error) {
    return (
      <WidgetContainer
        widgetTitle="Payments"
        textColor="text-grey-6 text-sm"
        bgColor="bg-white"
        widgetIcon={<FontAwesomeIcon icon={faCreditCard} className="text-grey-4" />}
        widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
        className={cn('h-min', className)}
      >
        <div className="p-4 text-center text-red-500">
          Error loading payment data
        </div>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer
      widgetTitle="Payments"
      textColor="text-grey-6 text-sm"
      bgColor="bg-white"
      widgetIcon={<FontAwesomeIcon icon={faCreditCard} className="text-grey-4" />}
      widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
      className={cn('h-min', className)}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3">
        <PaymentStatCard
          title="Total Payment"
          value={paymentData?.totalPayment?.amount || 0}
          count={paymentData?.totalPayment?.count || 0}
          icon={<CircleDollarIcon className="w-4 h-4" />}
          tooltip="Total amount of payments processed for this project"
          isLoading={isLoading}
        />
        <PaymentStatCard
          title="Total Amount Spent"
          value={paymentData?.totalAmountSpent?.amount || 0}
          count={paymentData?.totalAmountSpent?.count || 0}
          icon={<SackDollarIcon className="w-4 h-4" />}
          tooltip="Total amount spent on this project"
          isLoading={isLoading}
        />
        <PaymentStatCard
          title="Processing Payments"
          value={paymentData?.processingPayments?.amount || 0}
          count={paymentData?.processingPayments?.count || 0}
          icon={<ClockIcon className="w-4 h-4" />}
          tooltip="Payments currently being processed for this project"
          isLoading={isLoading}
        />
        <PaymentStatCard
          title="Paid For Briefs"
          value={paymentData?.paidForBriefs?.amount || 0}
          count={paymentData?.paidForBriefs?.count || 0}
          icon={<FileIcon className="w-4 h-4" />}
          tooltip="Payments made for briefs in this project"
          isLoading={isLoading}
        />
        <PaymentStatCard
          title="Paid From Sales Tracking"
          value={paymentData?.paidFromSalesTracking?.amount || 0}
          count={paymentData?.paidFromSalesTracking?.count || 0}
          icon={<TagsIcon className="w-4 h-4" />}
          tooltip="Payments from sales tracking for this project"
          isLoading={isLoading}
        />
      </div>
    </WidgetContainer>
  );
};
